import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { readingBeeAPI, narrativeAPI } from '@/lib/api';
import { BrutalCard, BrutalButton, BrutalBadge } from '@/components/brutal';
import { Trophy, Plus, Users, BookOpen, Clock, Award, Globe, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const EMOJI_OPTIONS = ['🏆', '🎯', '📚', '⭐', '🎨', '🎉', '🔥', '🌟', '📖', '🎖️', '🐝', '🎭', '✨', '🚀'];

/**
 * Admin: create and manage system-wide Reading Bees (scope=platform).
 * Lists every Bee on the platform, lets admin create one visible to ALL students.
 */
const AdminReadingBeesTab = () => {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedStatsId, setExpandedStatsId] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', narrative_id: '',
    prize_description: '', start_date: '', end_date: '',
    cover_emoji: '🏆',
  });

  const resetForm = () => setForm({
    title: '', description: '', narrative_id: '',
    prize_description: '', start_date: '', end_date: '',
    cover_emoji: '🏆',
  });

  const startEdit = (bee) => {
    setEditingId(bee.id);
    setShowCreate(true);
    setForm({
      title: bee.title || '',
      description: bee.description || '',
      narrative_id: bee.narrative_id || '',
      prize_description: bee.prize_description || '',
      start_date: bee.start_date || '',
      end_date: bee.end_date || '',
      cover_emoji: bee.cover_emoji || '🏆',
    });
  };

  const { data: bees = [], isLoading } = useQuery({
    queryKey: ['admin-reading-bees', showArchived],
    queryFn: async () => (await readingBeeAPI.getAll({ includeArchived: showArchived })).data,
  });

  const archiveMut = useMutation({
    mutationFn: (id) => readingBeeAPI.archive(id),
    onSuccess: () => { queryClient.invalidateQueries(['admin-reading-bees']); toast.success('Archived'); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to archive'),
  });

  const unarchiveMut = useMutation({
    mutationFn: (id) => readingBeeAPI.unarchive(id),
    onSuccess: () => { queryClient.invalidateQueries(['admin-reading-bees']); toast.success('Restored'); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to restore'),
  });

  // Pull all narratives the admin can see (admin role returns everything).
  const { data: narratives = [] } = useQuery({
    queryKey: ['admin-narratives-for-bee'],
    queryFn: async () => {
      try { return (await narrativeAPI.getAll()).data || []; }
      catch { return []; }
    },
    enabled: showCreate,
  });

  const createMut = useMutation({
    mutationFn: (data) => readingBeeAPI.create({ ...data, scope: 'platform' }),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-reading-bees']);
      queryClient.invalidateQueries(['reading-bees']);
      setShowCreate(false);
      setEditingId(null);
      resetForm();
      toast.success('Platform-wide Reading Bee created! All students will see it.');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create'),
  });

  const editMut = useMutation({
    mutationFn: ({ id, data }) => readingBeeAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-reading-bees']);
      queryClient.invalidateQueries(['reading-bees']);
      setShowCreate(false);
      setEditingId(null);
      resetForm();
      toast.success('Updated!');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to update'),
  });

  const handleSave = () => {
    if (editingId) editMut.mutate({ id: editingId, data: form });
    else createMut.mutate(form);
  };

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => readingBeeAPI.update(id, data),
    onSuccess: () => queryClient.invalidateQueries(['admin-reading-bees']),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => readingBeeAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-reading-bees']);
      toast.success('Deleted');
    },
  });

  const statusColors = { open: 'emerald', voting: 'amber', closed: 'rose' };

  if (isLoading) return <div className="text-center py-12 text-2xl font-bold">Loading…</div>;

  const platformBees = bees.filter((b) => b.scope === 'platform');
  const otherBees = bees.filter((b) => b.scope !== 'platform');

  return (
    <div className="space-y-6" data-testid="admin-reading-bees-tab">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-black uppercase flex items-center gap-2">
            <Trophy size={28} className="text-amber-500" /> Reading Bees
          </h2>
          <p className="text-sm text-gray-500 font-medium">
            Create platform-wide Reading Bees visible to every student. Or browse parent/teacher bees for oversight.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowArchived((v) => !v)}
            className={`px-3 py-2 border-4 border-black text-xs font-bold uppercase ${showArchived ? 'bg-amber-200' : 'bg-white hover:bg-amber-100'}`}
            data-testid="admin-toggle-archived"
          >
            {showArchived ? 'Hide archived' : 'Show archived'}
          </button>
          <BrutalButton variant="amber" onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-2">
            <Plus size={16} /> Create System-Wide Bee
          </BrutalButton>
        </div>
      </div>

      {showCreate && (
        <BrutalCard shadow="lg" className="bg-amber-50">
          <div className="flex items-center gap-2 mb-4">
            <Globe size={20} className="text-amber-600" />
            <h3 className="text-xl font-black uppercase">
              {editingId ? 'Edit Reading Bee' : 'Create Platform-Wide Reading Bee'}
            </h3>
          </div>
          {!editingId && (
            <p className="text-xs text-gray-600 mb-4">
              ⚠️ This Reading Bee will appear on every student's home screen across the entire platform.
            </p>
          )}

          {/* Emoji picker — cover emoji for cards + student banner */}
          <div className="mb-4">
            <label className="text-xs font-bold uppercase text-gray-600 block mb-2">Cover Emoji</label>
            <div className="flex flex-wrap gap-2">
              {EMOJI_OPTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => setForm({ ...form, cover_emoji: emoji })}
                  className={`text-2xl w-12 h-12 border-4 border-black flex items-center justify-center ${form.cover_emoji === emoji ? 'bg-amber-300' : 'bg-white hover:bg-amber-100'}`}
                  aria-label={`Pick ${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs font-bold uppercase text-gray-600 block mb-1">Title</label>
              <input className="w-full px-3 py-2 border-4 border-black font-bold"
                placeholder="Spring Platform Reading Bee"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-gray-600 block mb-1">Select Story</label>
              <select className="w-full px-3 py-2 border-4 border-black font-bold text-sm bg-white"
                value={form.narrative_id}
                onChange={(e) => setForm({ ...form, narrative_id: e.target.value })}
              >
                <option value="">-- Choose a story --</option>
                {narratives.map((n) => (
                  <option key={n.id} value={n.id}>{n.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-gray-600 block mb-1">Start Date</label>
              <input type="date" className="w-full px-3 py-2 border-4 border-black font-bold"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-gray-600 block mb-1">End Date</label>
              <input type="date" className="w-full px-3 py-2 border-4 border-black font-bold"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase text-gray-600 block mb-1">Prize (optional)</label>
              <input className="w-full px-3 py-2 border-4 border-black font-bold"
                placeholder="e.g. $100 gift card, free annual subscription"
                value={form.prize_description}
                onChange={(e) => setForm({ ...form, prize_description: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase text-gray-600 block mb-1">Description</label>
              <textarea className="w-full px-3 py-2 border-4 border-black font-bold" rows={2}
                placeholder="Read the assigned chapter aloud with expression. Record your best take!"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <BrutalButton
              variant="emerald"
              onClick={handleSave}
              disabled={!form.title || !form.narrative_id || !form.start_date || !form.end_date}
            >
              {editingId ? 'Save Changes' : 'Launch System-Wide'}
            </BrutalButton>
            {editingId && (
              <BrutalButton
                variant="ghost"
                onClick={() => { setEditingId(null); setShowCreate(false); resetForm(); }}
              >
                Cancel
              </BrutalButton>
            )}
          </div>
        </BrutalCard>
      )}

      <div>
        <h3 className="text-lg font-black uppercase mb-3 flex items-center gap-2">
          <Globe size={18} /> Platform-Wide Bees ({platformBees.length})
        </h3>
        {platformBees.length === 0 ? (
          <BrutalCard className="text-center py-8 text-gray-500">
            <Globe size={32} className="mx-auto mb-2 text-gray-400" />
            <p className="font-bold">No system-wide Reading Bees yet.</p>
            <p className="text-sm">Click "Create System-Wide Bee" to launch one visible to everyone.</p>
          </BrutalCard>
        ) : (
          <div className="space-y-3">
            {platformBees.map((bee) => (
              <BrutalCard key={bee.id}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-2xl" aria-hidden>{bee.cover_emoji || '🏆'}</span>
                      <h4 className="text-lg font-black">{bee.title}</h4>
                      <BrutalBadge variant={statusColors[bee.status] || 'indigo'} size="sm">{bee.status}</BrutalBadge>
                      <BrutalBadge variant="amber" size="sm">platform</BrutalBadge>
                      {bee.archived && <BrutalBadge variant="ghost" size="sm">archived</BrutalBadge>}
                      {bee.winner_entry_id && bee.status === 'closed' && (
                        <BrutalBadge variant="emerald" size="sm">🏅 winner preserved</BrutalBadge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      <BookOpen size={12} className="inline mr-1" />{bee.narrative_title}
                      {' · '}<Users size={12} className="inline mr-1" />{bee.entry_count} entries
                      {bee.prize_description && <>{' · '}<Award size={12} className="inline mr-1" />{bee.prize_description}</>}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      <Clock size={10} className="inline mr-1" />{bee.start_date} — {bee.end_date}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end">
                    <BrutalButton variant="ghost" size="sm" onClick={() => startEdit(bee)}>
                      Edit
                    </BrutalButton>
                    {bee.archived ? (
                      <BrutalButton variant="emerald" size="sm" onClick={() => unarchiveMut.mutate(bee.id)}>
                        Restore
                      </BrutalButton>
                    ) : (
                      <BrutalButton variant="ghost" size="sm" onClick={() => archiveMut.mutate(bee.id)}>
                        Archive
                      </BrutalButton>
                    )}
                    <BrutalButton
                      variant="indigo"
                      size="sm"
                      onClick={() => setExpandedStatsId(expandedStatsId === bee.id ? null : bee.id)}
                      className="flex items-center gap-1"
                    >
                      <BarChart3 size={14} /> {expandedStatsId === bee.id ? 'Hide' : 'Stats'}
                    </BrutalButton>
                    {bee.status === 'open' && (
                      <BrutalButton variant="amber" size="sm" onClick={() => updateMut.mutate({ id: bee.id, data: { status: 'voting' } })}>
                        Start Voting
                      </BrutalButton>
                    )}
                    {bee.status === 'voting' && (
                      <BrutalButton variant="rose" size="sm" onClick={() => updateMut.mutate({ id: bee.id, data: { status: 'closed' } })}>
                        Close
                      </BrutalButton>
                    )}
                    <BrutalButton
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (window.confirm(`Delete "${bee.title}"? This cannot be undone.`)) deleteMut.mutate(bee.id);
                      }}
                    >
                      Delete
                    </BrutalButton>
                  </div>
                </div>
                {expandedStatsId === bee.id && (
                  <div className="mt-4 pt-4 border-t-4 border-black">
                    <ParticipationPanel beeId={bee.id} />
                  </div>
                )}
              </BrutalCard>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-lg font-black uppercase mb-3">
          Parent & Teacher Bees ({otherBees.length})
        </h3>
        {otherBees.length === 0 ? (
          <p className="text-sm text-gray-500 italic">None yet.</p>
        ) : (
          <div className="space-y-2">
            {otherBees.map((bee) => (
              <div key={bee.id} className="border-4 border-black bg-white p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-xl" aria-hidden>{bee.cover_emoji || '🏆'}</span>
                    <div>
                      <p className="font-bold text-sm">{bee.title}</p>
                      <p className="text-xs text-gray-500">
                        scope: {bee.scope} · by {bee.creator_name} · {bee.narrative_title} · {bee.entry_count} entries
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <BrutalBadge variant={statusColors[bee.status] || 'indigo'} size="sm">{bee.status}</BrutalBadge>
                    <button
                      onClick={() => setExpandedStatsId(expandedStatsId === bee.id ? null : bee.id)}
                      className="text-xs font-bold uppercase px-2 py-1 border-2 border-black bg-white hover:bg-indigo-100 flex items-center gap-1"
                    >
                      <BarChart3 size={12} /> {expandedStatsId === bee.id ? 'Hide' : 'Stats'}
                    </button>
                  </div>
                </div>
                {expandedStatsId === bee.id && (
                  <div className="mt-3 pt-3 border-t-2 border-black">
                    <ParticipationPanel beeId={bee.id} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Loads /reading-bees/{id}/participation and renders a per-classroom breakdown.
const ParticipationPanel = ({ beeId }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reading-bee-participation', beeId],
    queryFn: async () => (await readingBeeAPI.participation(beeId)).data,
    enabled: !!beeId,
  });

  if (isLoading) return <p className="text-sm text-gray-500">Loading participation…</p>;
  if (error) return <p className="text-sm text-rose-600">Couldn't load participation stats.</p>;
  if (!data) return null;

  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center gap-4 text-xs font-bold uppercase">
        <span>Scope: {data.scope}</span>
        <span>Total entries: {data.total_entries}</span>
        <span>Unique entrants: {data.unique_entrants}</span>
      </div>

      {data.platform && (
        <div className="border-l-4 border-black pl-3">
          <p className="font-bold">Platform-wide</p>
          <p className="text-xs text-gray-600">
            {data.platform.entries} / {data.platform.total_students_on_platform} students entered
            {' · '}<b>{data.platform.participation_pct}%</b> participation
          </p>
        </div>
      )}

      {data.classrooms?.length > 0 && (
        <div>
          <p className="font-bold uppercase text-xs mb-2">Per-Classroom</p>
          <table className="w-full border-4 border-black text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 text-left">Classroom</th>
                <th className="p-2 text-right">Members</th>
                <th className="p-2 text-right">Entered</th>
                <th className="p-2 text-right">Participation</th>
              </tr>
            </thead>
            <tbody>
              {data.classrooms.map((c) => (
                <tr key={c.classroom_id} className="border-t-2 border-black">
                  <td className="p-2 font-bold">{c.classroom_name}</td>
                  <td className="p-2 text-right">{c.total_members}</td>
                  <td className="p-2 text-right">{c.entered_count}</td>
                  <td className="p-2 text-right font-bold">
                    <span className={c.participation_pct >= 75 ? 'text-emerald-600' : c.participation_pct >= 25 ? 'text-amber-600' : 'text-rose-600'}>
                      {c.participation_pct}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.direct_students?.length > 0 && (
        <div>
          <p className="font-bold uppercase text-xs mb-2">Directly Targeted Students</p>
          <div className="flex flex-wrap gap-2">
            {data.direct_students.map((s) => (
              <span
                key={s.student_id}
                className={`border-2 border-black px-2 py-1 text-xs ${s.entered ? 'bg-emerald-200' : 'bg-gray-100'}`}
              >
                {s.entered ? '✓ ' : '○ '}{s.student_name}
              </span>
            ))}
          </div>
        </div>
      )}

      {!data.classrooms?.length && !data.direct_students?.length && !data.platform && (
        <p className="text-xs text-gray-500 italic">
          This bee has no specific classroom or student targets.
        </p>
      )}
    </div>
  );
};

export default AdminReadingBeesTab;
